import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Search,
  Plus,
  User2,
  Phone,
  Building2,
  Shield,
  RefreshCw,
  ChevronDown,
  Unlink,
} from "lucide-react";
import { ListSubheader, Menu, MenuItem } from "@mui/material";
import AddContactModal from "../modals/AddContactModal";
import ContactAddedModal from "../modals/ContactAddedModal";
import FrontContactSyncModal from "../modals/FrontContactSyncModal";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import posthog from "posthog-js";
import {
  buildFrontContactSyncAuthorizeUrl,
  parseFrontContactSyncFromOrg,
  parseOAuthCallbackState,
  isFrontSyncInProgress,
  isFrontSyncConnected,
  getFrontSyncStatusLabel,
} from "../../utils/frontOAuth";
import { formatTimestampForZone } from "../utils/formattimestamp";
import { useOrgUsers, getAssignedUserLabel } from "../../hooks/useOrgUsers";

function getUrlParam(paramName, url = window.location.href) {
  const urlObj = new URL(url);
  return urlObj.searchParams.get(paramName);
}

export default function Contacts() {
  const [orgData, setOrgData] = useState(() =>
    JSON.parse(localStorage.getItem("organization") || "{}")
  );
  const organizationId = orgData?.orgId?.S;

  const { userNameById } = useOrgUsers(organizationId);

  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [openAddContactModal, setOpenAddContactModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [searchLoader, setSearchLoader] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [flagsLoaded, setFlagsLoaded] = useState(false);

  const [openContactAddedModal, setOpenContactAddedModal] = useState(false);
  const [name, setName] = useState("");

  const [frontSync, setFrontSync] = useState(() =>
    parseFrontContactSyncFromOrg(
      JSON.parse(localStorage.getItem("organization") || "{}")
    )
  );
  const [syncActionLoading, setSyncActionLoading] = useState(false);
  const [oauthCode, setOauthCode] = useState(null);
  const [openFrontSyncModal, setOpenFrontSyncModal] = useState(false);
  const [syncMenuAnchor, setSyncMenuAnchor] = useState(null);

  const navigate = useNavigate();

  const refreshOrgFromServer = useCallback(async () => {
    if (!organizationId) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}get-organization`,
        { orgId: organizationId }
      );
      const updatedOrg = response.data?.organization;
      if (updatedOrg) {
        localStorage.setItem("organization", JSON.stringify(updatedOrg));
        setOrgData(updatedOrg);
        setFrontSync(parseFrontContactSyncFromOrg(updatedOrg));
      }
    } catch (err) {
      console.error("Failed to refresh organization:", err);
    }
  }, [organizationId]);

  // OAuth callback on /dashboard/contacts
  useEffect(() => {
    const code = getUrlParam("code");
    if (!code) return;

    const state = getUrlParam("state");
    const parsed = parseOAuthCallbackState(state);
    if (parsed?.provider === "front_contact_sync") {
      setOauthCode(code);
      setOpenFrontSyncModal(true);
    }
  }, []);

  useEffect(() => {
    refreshOrgFromServer();
  }, [refreshOrgFromServer]);

  // Poll while Lambda sync is in progress
  useEffect(() => {
    if (!isFrontSyncInProgress(frontSync.status)) return;

    const interval = setInterval(() => {
      refreshOrgFromServer();
      refreshContacts();
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontSync.status, organizationId]);

  // Check feature flag on mount
  useEffect(() => {
    // Ensure flags are loaded before usage (for first time visitors)
    // This callback fires immediately if flags are already loaded
    posthog.onFeatureFlags(function() {
      const enabled = posthog.isFeatureEnabled('contacts_page');
      setIsFeatureEnabled(enabled || false);
      setFlagsLoaded(true);
    });

    // Also check directly in case flags are already available (for subsequent visits)
    // This provides immediate feedback if flags are already loaded
    const directCheck = posthog.isFeatureEnabled('contacts_page');
    if (directCheck !== undefined && directCheck !== null) {
      setIsFeatureEnabled(directCheck);
      setFlagsLoaded(true);
    }
  }, []);

  // Fetch contacts from API
  const fetchContacts = useCallback(async (searchTerm = "", lastKey = null) => {
    if (!organizationId) return;

    const isLoadingSearch = searchTerm.trim().length > 0;
    if (isLoadingSearch) {
      setSearchLoader(true);
    } else {
      setLoader(true);
    }

    try {
      const endpoint = searchTerm.trim() ? "search-contacts" : "get-contacts";
      const requestBody = {
        organizationId,
        limit: 1000,
      };

      if (searchTerm.trim()) {
        // Send single query field - backend searches across name, company, and phone number
        requestBody.query = searchTerm.trim();
      }

      if (lastKey) {
        requestBody.lastEvaluatedKey = lastKey;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        requestBody
      );

      if (response.data.status === "success") {
        const fetchedContacts = response.data.contacts || [];
        
        if (lastKey) {
          // Append for pagination
          setContacts((prev) => [...prev, ...fetchedContacts]);
        } else {
          // Replace for new search or initial load
          setContacts(fetchedContacts);
        }

        setLastEvaluatedKey(response.data.lastEvaluatedKey || null);
        setHasMore(response.data.hasMore || false);
      }
    } catch (err) {
      // Log for production diagnostics when contacts fetch fails
      console.error("Error fetching contacts:", err);
      setContacts([]);
      setLastEvaluatedKey(null);
      setHasMore(false);
    } finally {
      setLoader(false);
      setSearchLoader(false);
    }
  }, [organizationId]);

  // Initial load
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounced search
  useEffect(() => {
    // Reset pagination when search changes
    setLastEvaluatedKey(null);
    setHasMore(false);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      if (search.trim()) {
        fetchContacts(search);
      } else {
        // If search is cleared, fetch all contacts
        fetchContacts();
      }
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, fetchContacts]);

  // Load more contacts (pagination)
  const loadMore = () => {
    if (!hasMore || loader || searchLoader) return;
    fetchContacts(search, lastEvaluatedKey);
  };
  // Refresh contacts list (used after adding a contact)
  const refreshContacts = () => {
    setLastEvaluatedKey(null);
    setHasMore(false);
    fetchContacts(search);
  };

  const isFrontConnected = isFrontSyncConnected(frontSync.status);
  const isSyncInProgress = isFrontSyncInProgress(frontSync.status);

  const openSyncMenu = (event) => {
    setSyncMenuAnchor(event.currentTarget);
  };

  const closeSyncMenu = () => {
    setSyncMenuAnchor(null);
  };

  const handleConnectFront = () => {
    closeSyncMenu();
    if (!organizationId) return;
    window.location.href = buildFrontContactSyncAuthorizeUrl(organizationId);
  };

  const handleSyncNow = async () => {
    closeSyncMenu();
    if (!organizationId || syncActionLoading) return;
    if (isFrontSyncInProgress(frontSync.status)) return;

    setSyncActionLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}front/contact-sync/run`,
        { organization_id: organizationId }
      );
      if (response.data?.updatedItem) {
        localStorage.setItem(
          "organization",
          JSON.stringify(response.data.updatedItem)
        );
        setOrgData(response.data.updatedItem);
        setFrontSync(parseFrontContactSyncFromOrg(response.data.updatedItem));
      }
    } catch (err) {
      console.error("Front contact sync failed:", err);
      await refreshOrgFromServer();
    } finally {
      setSyncActionLoading(false);
    }
  };

  const handleRemoveSync = async () => {
    closeSyncMenu();
    if (!organizationId || syncActionLoading) return;
    setSyncActionLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}front/contact-sync/remove`,
        { organization_id: organizationId }
      );
      if (response.data?.updatedItem) {
        localStorage.setItem(
          "organization",
          JSON.stringify(response.data.updatedItem)
        );
        setOrgData(response.data.updatedItem);
        setFrontSync(parseFrontContactSyncFromOrg(response.data.updatedItem));
      }
    } catch (err) {
      console.error("Failed to remove Front contact sync:", err);
    } finally {
      setSyncActionLoading(false);
    }
  };

  // Show loading state while checking feature flag
  if (!flagsLoaded) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // Show access denied if feature flag is disabled
  if (!isFeatureEnabled) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Access Restricted</h1>
          <p className="text-gray-600 mb-4">This feature is not available for your account.</p>
          <p className="text-gray-600">
            Need help?{" "}
            <a
              href="mailto:hello@twilanswer.com"
              className="text-[#4F46E5] hover:text-[#4338CA] underline"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full min-w-0">
      <FrontContactSyncModal
        open={openFrontSyncModal}
        setOpen={setOpenFrontSyncModal}
        code={oauthCode}
        onSuccess={() => {
          refreshOrgFromServer();
          refreshContacts();
        }}
      />
      <ContactAddedModal
        open={openContactAddedModal}
        setOpen={setOpenContactAddedModal}
        name={name}
      />
      <AddContactModal
        open={openAddContactModal}
        setOpen={setOpenAddContactModal}
        loader={loader}
        setLoader={setLoader}
        users={contacts}
        setUsers={setContacts}
        setOpenContactAddedModal={setOpenContactAddedModal}
        setName={setName}
        onContactAdded={refreshContacts}
      />

      {/* Header */}
      <div className="flex w-full items-center justify-between gap-4 mb-6">
        <h1 className="text-[26px] font-semibold text-gray-800 shrink-0">
          Contacts
        </h1>

        <div className="flex items-center gap-2 shrink-0 ml-auto">
          <button
            type="button"
            onClick={openSyncMenu}
            className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium shadow-sm whitespace-nowrap"
          >
            {isSyncInProgress && (
              <RefreshCw size={16} className="animate-spin text-amber-600" />
            )}
            Contact sync
            <ChevronDown size={16} className="text-gray-500" />
          </button>
          <button
            onClick={() => setOpenAddContactModal(true)}
            className="flex items-center gap-2 bg-brand-primary hover:bg-brand-hover text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm whitespace-nowrap"
          >
            <Plus size={18} /> Add Contact
          </button>
        </div>
      </div>

      <Menu
        anchorEl={syncMenuAnchor}
        open={Boolean(syncMenuAnchor)}
        onClose={closeSyncMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { minWidth: 280, mt: 1 },
          },
        }}
      >
        <ListSubheader sx={{ lineHeight: 1.4, py: 1.5 }}>
          Setup a contact sync
        </ListSubheader>
        {!isFrontConnected ? (
          <MenuItem onClick={handleConnectFront}>
            <div className="py-0.5">
              <div className="text-sm font-medium text-gray-900">Front</div>
              <div className="text-xs text-gray-500">
                Import contacts from your Front workspace
              </div>
            </div>
          </MenuItem>
        ) : (
          <>
            <MenuItem disabled sx={{ opacity: "1 !important" }}>
              <div className="py-0.5 w-full">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-gray-900">Front</span>
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      isSyncInProgress
                        ? "bg-amber-100 text-amber-800"
                        : frontSync.status === "connected"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                    }`}
                  >
                    {getFrontSyncStatusLabel(frontSync.status)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {isSyncInProgress
                    ? "Import may take a minute for large workspaces."
                    : frontSync.last_synced_at
                      ? `Last synced ${formatTimestampForZone(frontSync.last_synced_at)}`
                      : "Connected"}
                  {frontSync.front_teammate_email
                    ? ` · ${frontSync.front_teammate_email}`
                    : ""}
                </div>
                {frontSync.status === "error" && frontSync.last_error && (
                  <div className="text-xs text-red-600 mt-1">
                    {frontSync.last_error}
                  </div>
                )}
              </div>
            </MenuItem>
            <MenuItem
              onClick={handleSyncNow}
              disabled={syncActionLoading || isSyncInProgress}
            >
              <RefreshCw
                size={16}
                className={`mr-2 ${
                  syncActionLoading || isSyncInProgress ? "animate-spin" : ""
                }`}
              />
              Sync now
            </MenuItem>
            <MenuItem
              onClick={handleRemoveSync}
              disabled={syncActionLoading || isSyncInProgress}
              sx={{ color: "error.main" }}
            >
              <Unlink size={16} className="mr-2" />
              Remove Front sync
            </MenuItem>
          </>
        )}
      </Menu>

      {isSyncInProgress && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <RefreshCw size={16} className="animate-spin shrink-0" />
          <span>
            Importing contacts from Front. New contacts will appear in the list
            as they are synced.
          </span>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white border border-gray-200 rounded-t-xl p-4 shadow-sm">
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <Search size={18} className="text-gray-700" />
          <input
            placeholder="Search contacts by name, phone, or company..."
            className="w-full bg-transparent outline-none text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {(searchLoader || loader) && (
            <div className="text-gray-400 text-xs">Searching...</div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto">
        <div className="min-w-[900px] bg-white border border-gray-200 border-t-white shadow-sm overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-5 gap-6 px-6 py-3 text-[15px] font-medium text-gray-500 border-b bg-gray-50">
            <div>Name</div>
            <div>Phone</div>
            <div>Company</div>
            <div>Assigned to</div>
            <div>Source</div>
          </div>

          {/* Loading State */}
          {loader && contacts.length === 0 && (
            <div className="px-6 py-6 text-gray-500 text-center">
              Loading contacts...
            </div>
          )}

          {/* Rows */}
          {contacts.map((c, i) => (
            <div
              key={`${c.phoneNumber}-${i}`}
              className="grid grid-cols-5 gap-6 px-6 py-4 border-b text-[14px] last:border-none hover:bg-gray-50 transition cursor-pointer"
              onClick={() => {
                navigate("/dashboard/contact/" + c.phoneNumber);
              }}
            >
              {/* Name */}
              <div className="flex items-center gap-3 text-gray-800 font-bold whitespace-nowrap">
                <div className="h-10 w-10 bg-[#E0E7FF] rounded-full flex items-center justify-center">
                  <User2 size={20} className="text-[#4F46E5]" />
                </div>
                {c.name}
              </div>

              {/* Phone */}
              <div className="flex items-center gap-2 text-gray-700 whitespace-nowrap">
                <Phone size={16} className="text-gray-400" />
                {c.phoneNumber}
              </div>

              {/* Company */}
              <div className="flex items-center gap-2 text-gray-700 whitespace-nowrap">
                <Building2 size={16} className="text-gray-400" />
                {c.company}
              </div>

              {/* Assigned to */}
              <div className="flex items-center gap-2 text-gray-700 whitespace-nowrap">
                <User2 size={16} className="text-gray-400" />
                {getAssignedUserLabel(c.assignedUserId, userNameById)}
              </div>

              {/* Source */}
              <div className="flex items-center whitespace-nowrap">
                {c.source === "manual" ? (
                  <span className="px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                    {c.source.charAt(0).toUpperCase() + c.source.slice(1)}
                  </span>
                ) : (
                  <span className="px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-600 flex items-center gap-1">
                    <Shield size={14} />{" "}
                    {c.source
                      ? c.source.charAt(0).toUpperCase() + c.source.slice(1)
                      : "Unknown"}
                  </span>
                )}
              </div>
            </div>
          ))}

          {/* No Results */}
          {!loader && !searchLoader && contacts.length === 0 && (
            <div className="px-6 py-6 text-gray-500 text-center">
              {search.trim()
                ? "No contacts match your search."
                : "No contacts found. Add your first contact to get started."}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && !loader && !searchLoader && (
            <div className="px-6 py-4 text-center border-t">
              <button
                onClick={loadMore}
                className="px-4 py-2 text-sm text-[#4F46E5] hover:text-[#4338CA] font-medium"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
